import { useEffect, useState, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import type {
  RichTextDocument,
  RichTextMark,
  RichTextNode,
} from '@servicesai/api-client';

export const EMPTY_RICH_TEXT_DOCUMENT: RichTextDocument = {
  type: 'doc',
  content: [{ type: 'paragraph', content: [] }],
};

export function plainTextToRichDocument(value: string): RichTextDocument {
  const paragraphs = value.split(/\n{2,}/u);
  return {
    type: 'doc',
    content: paragraphs.map((paragraph) => ({
      type: 'paragraph',
      content: paragraph
        ? paragraph.split('\n').flatMap((line, index) => [
            ...(index > 0 ? [{ type: 'hardBreak' as const }] : []),
            ...(line ? [{ type: 'text' as const, text: line }] : []),
          ])
        : [],
    })),
  };
}

function safeHref(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if ((value.startsWith('/') && !value.startsWith('//')) || value.startsWith('https://') || value.startsWith('mailto:')) {
    return value;
  }
  return null;
}

function renderMarks(content: ReactNode, marks: RichTextMark[] = []): ReactNode {
  return marks.reduce<ReactNode>((child, mark, index) => {
    const key = `${mark.type}-${index}`;
    switch (mark.type) {
      case 'bold':
        return <strong key={key} className="font-bold">{child}</strong>;
      case 'italic':
        return <em key={key}>{child}</em>;
      case 'underline':
        return <u key={key}>{child}</u>;
      case 'strike':
        return <s key={key}>{child}</s>;
      case 'code':
        return <code key={key} className="rounded bg-aeblack-50 px-1 py-0.5 font-mono text-[0.9em]">{child}</code>;
      case 'link': {
        const href = safeHref(mark.attrs?.href);
        return href
          ? <a key={key} href={href} target="_blank" rel="noreferrer noopener" className="font-semibold text-oasis underline underline-offset-2">{child}</a>
          : child;
      }
    }
  }, content);
}

function renderNodes(nodes: RichTextNode[] | undefined, prefix: string): ReactNode[] {
  return (nodes ?? []).map((node, index) => renderNode(node, `${prefix}-${index}`));
}

function renderNode(node: RichTextNode, key: string): ReactNode {
  const children = renderNodes(node.content, key);
  switch (node.type) {
    case 'text':
      return <span key={key}>{renderMarks(node.text ?? '', node.marks)}</span>;
    case 'paragraph':
      return <p key={key} className="my-2 first:mt-0 last:mb-0">{children.length ? children : <br />}</p>;
    case 'heading': {
      const level = Number(node.attrs?.level ?? 2);
      if (level === 1) return <h1 key={key} className="mb-2 mt-4 text-xl font-extrabold first:mt-0">{children}</h1>;
      if (level === 3) return <h3 key={key} className="mb-1 mt-3 text-sm font-extrabold first:mt-0">{children}</h3>;
      return <h2 key={key} className="mb-1.5 mt-3 text-base font-extrabold first:mt-0">{children}</h2>;
    }
    case 'bulletList':
      return <ul key={key} className="my-2 list-disc space-y-1 ps-5">{children}</ul>;
    case 'orderedList':
      return <ol key={key} className="my-2 list-decimal space-y-1 ps-5">{children}</ol>;
    case 'listItem':
      return <li key={key}>{children}</li>;
    case 'taskList':
      return <ul key={key} className="my-2 list-none space-y-1 ps-0">{children}</ul>;
    case 'taskItem':
      return (
        <li key={key} className="flex items-start gap-2">
          <input type="checkbox" checked={node.attrs?.checked === true} readOnly disabled className="mt-1 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">{children}</div>
        </li>
      );
    case 'blockquote':
      return <blockquote key={key} className="my-2 border-s-2 border-aegold-400 ps-3 text-ink-soft italic">{children}</blockquote>;
    case 'codeBlock':
      return <pre key={key} className="my-2 overflow-x-auto rounded-lg bg-aeblack-50 p-3 text-xs"><code>{children}</code></pre>;
    case 'horizontalRule':
      return <hr key={key} className="my-3 border-aeblack-100" />;
    case 'hardBreak':
      return <br key={key} />;
  }
}

export function RichTextDocumentView({
  document,
  fallback,
  className = '',
}: {
  document: RichTextDocument | null;
  fallback: string;
  className?: string;
}) {
  const value = document ?? plainTextToRichDocument(fallback);
  return (
    <div className={`text-sm leading-relaxed text-ink-soft ${className}`}>
      {renderNodes(value.content, 'document')}
    </div>
  );
}

function ToolbarButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={[
        'min-h-8 rounded-md border px-2 text-xs font-bold transition',
        active
          ? 'border-aegold-400 bg-aegold-100 text-ink'
          : 'border-aeblack-100 bg-white text-ink-soft hover:bg-aeblack-50',
        disabled ? 'cursor-not-allowed opacity-40' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  fallbackText = '',
  onChange,
  lang,
  maxLength = 50_000,
  ariaLabel,
}: {
  value: RichTextDocument | null;
  fallbackText?: string;
  onChange: (document: RichTextDocument, plainText: string) => void;
  lang: 'en' | 'ar';
  maxLength?: number;
  ariaLabel: string;
}) {
  const [linkError, setLinkError] = useState<string | null>(null);
  const initialContent = value ?? plainTextToRichDocument(fallbackText);
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          protocols: ['https', 'mailto'],
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
        },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        dir: lang === 'ar' ? 'rtl' : 'ltr',
      },
    },
    onUpdate: ({ editor: current }) => {
      const plainText = current.getText({ blockSeparator: '\n\n' });
      onChange(current.getJSON() as RichTextDocument, plainText);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = value ?? plainTextToRichDocument(fallbackText);
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(next)) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, fallbackText, value]);

  if (!editor) return null;
  const textLength = editor.getText({ blockSeparator: '\n\n' }).length;
  const labels = lang === 'ar'
    ? {
        paragraph: 'فقرة',
        heading: 'عنوان',
        bold: 'عريض',
        italic: 'مائل',
        underline: 'تحته خط',
        strike: 'يتوسطه خط',
        code: 'شفرة',
        bullet: 'قائمة نقطية',
        ordered: 'قائمة مرقمة',
        tasks: 'قائمة مهام',
        quote: 'اقتباس',
        link: 'رابط',
        unlink: 'إزالة الرابط',
        undo: 'تراجع',
        redo: 'إعادة',
        invalidLink: 'استخدم رابط HTTPS أو mailto أو رابطاً داخلياً يبدأ بـ /.',
        linkPrompt: 'أدخل رابط HTTPS أو mailto أو رابطاً داخلياً',
      }
    : {
        paragraph: 'Paragraph',
        heading: 'Heading',
        bold: 'Bold',
        italic: 'Italic',
        underline: 'Underline',
        strike: 'Strikethrough',
        code: 'Inline code',
        bullet: 'Bulleted list',
        ordered: 'Numbered list',
        tasks: 'Task list',
        quote: 'Block quote',
        link: 'Add link',
        unlink: 'Remove link',
        undo: 'Undo',
        redo: 'Redo',
        invalidLink: 'Use an HTTPS, mailto, or internal link beginning with /.',
        linkPrompt: 'Enter an HTTPS, mailto, or internal link',
      };

  const addLink = () => {
    const current = editor.getAttributes('link').href as string | undefined;
    const href = window.prompt(labels.linkPrompt, current ?? 'https://')?.trim();
    if (!href) return;
    if (!safeHref(href)) {
      setLinkError(labels.invalidLink);
      return;
    }
    setLinkError(null);
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-aeblack-100 bg-white">
      <div className="flex flex-wrap gap-1.5 border-b border-aeblack-100 bg-aeblack-50 p-2" role="toolbar" aria-label={ariaLabel}>
        <ToolbarButton label={labels.paragraph} active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>¶</ToolbarButton>
        <ToolbarButton label={labels.heading} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolbarButton>
        <ToolbarButton label={labels.bold} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>B</ToolbarButton>
        <ToolbarButton label={labels.italic} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarButton>
        <ToolbarButton label={labels.underline} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></ToolbarButton>
        <ToolbarButton label={labels.strike} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></ToolbarButton>
        <ToolbarButton label={labels.code} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</ToolbarButton>
        <ToolbarButton label={labels.bullet} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</ToolbarButton>
        <ToolbarButton label={labels.ordered} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</ToolbarButton>
        <ToolbarButton label={labels.tasks} active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}>☑</ToolbarButton>
        <ToolbarButton label={labels.quote} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>“”</ToolbarButton>
        <ToolbarButton label={labels.link} active={editor.isActive('link')} onClick={addLink}>↗</ToolbarButton>
        <ToolbarButton label={labels.unlink} disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}>×↗</ToolbarButton>
        <span className="mx-1 border-s border-aeblack-100" aria-hidden />
        <ToolbarButton label={labels.undo} disabled={!editor.can().chain().focus().undo().run()} onClick={() => editor.chain().focus().undo().run()}>↶</ToolbarButton>
        <ToolbarButton label={labels.redo} disabled={!editor.can().chain().focus().redo().run()} onClick={() => editor.chain().focus().redo().run()}>↷</ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className={[
          '[&_.ProseMirror]:min-h-44 [&_.ProseMirror]:px-4 [&_.ProseMirror]:py-3 [&_.ProseMirror]:text-sm',
          '[&_.ProseMirror]:leading-relaxed [&_.ProseMirror]:text-ink-soft [&_.ProseMirror]:outline-none',
          '[&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h1]:mt-4 [&_.ProseMirror_h1]:text-xl [&_.ProseMirror_h1]:font-extrabold',
          '[&_.ProseMirror_h2]:mb-1.5 [&_.ProseMirror_h2]:mt-3 [&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:font-extrabold',
          '[&_.ProseMirror_h3]:mb-1 [&_.ProseMirror_h3]:mt-3 [&_.ProseMirror_h3]:text-sm [&_.ProseMirror_h3]:font-extrabold',
          '[&_.ProseMirror_p]:my-2 [&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ps-5',
          '[&_.ProseMirror_ol]:my-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ps-5',
          '[&_.ProseMirror_blockquote]:my-2 [&_.ProseMirror_blockquote]:border-s-2 [&_.ProseMirror_blockquote]:border-aegold-400 [&_.ProseMirror_blockquote]:ps-3',
          '[&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:bg-aeblack-50 [&_.ProseMirror_pre]:p-3',
          '[&_.ProseMirror_a]:font-semibold [&_.ProseMirror_a]:text-oasis [&_.ProseMirror_a]:underline',
          '[&_.ProseMirror_ul[data-type=taskList]]:list-none [&_.ProseMirror_ul[data-type=taskList]]:ps-0',
          '[&_.ProseMirror_li[data-type=taskItem]]:flex [&_.ProseMirror_li[data-type=taskItem]]:items-start [&_.ProseMirror_li[data-type=taskItem]]:gap-2',
        ].join(' ')}
      />
      <div className="flex items-center justify-between gap-3 border-t border-aeblack-100 px-3 py-2 text-[10px]">
        <span className="text-error">{linkError}</span>
        <span className={textLength > maxLength ? 'font-bold text-error' : 'text-ink-faint'}>
          {textLength.toLocaleString()} / {maxLength.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
